import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { UserSummaryType } from '../../../../../../../../api/types/user/user-summary.type';
import { Checkbox } from 'primereact/checkbox';
import { EnrollmentWithUserDb } from '../../../../../../../../api/types/programs/course/exams/exam-sessions-enrollment-entity';
import { Button } from 'primereact/button';
import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ClassSchedule,
  ExamSessionDetails,
} from '../../../../../../../../api/types/programs/course/exams/response/exam-metadata';
import globalCourseStore from '../../../../../../../../stores/global-course-store';
import { RequestEnrollment } from '../../../../../../../../api/types/programs/course/exams/requests/request-exam-enrollment-update';
import globalProgramStore from '../../../../../../../../stores/program-store';
import { upsertStudentsEnrollmentOperations } from '../../componenets/students-enrollment/operations/upsert-students-enrollment-operations';

const columnStyle: CSSProperties = {
  textAlign: 'right',
  minWidth: '30px',
  maxWidth: '30px',
};

interface ExamPageStudentsSchedulingDatamodel {
  examId: number;
  examSession: ExamSessionDetails | null;
  setSessionDetails: (sessions: ExamSessionDetails[]) => any;
}

export const ExamPageStudentsScheduling: React.FC<
  ExamPageStudentsSchedulingDatamodel
> = ({ examId, examSession, setSessionDetails }) => {
  const currentCourse = globalCourseStore.getCurrentCourse();

  const [studentClassRegistrations, setStudentClassRegistrations] = useState<
    Record<number, RequestEnrollment>
  >({});

  const initialEnrollmentState = useRef<Record<number, RequestEnrollment>>({});

  const initializeStudentRegistrations = (
    examSession: ExamSessionDetails,
  ): Record<number, RequestEnrollment> => {
    const initialRegistrations: Record<number, RequestEnrollment> = {};

    examSession.classesSchedule.forEach((classSchedule: ClassSchedule) => {
      classSchedule.enrollment.forEach((enrollment: EnrollmentWithUserDb) => {
        initialRegistrations[enrollment.studentId] = {
          studentId: enrollment.studentId,
          scheduleId: enrollment.scheduleId,
        };
      });
    });

    return initialRegistrations;
  };

  useEffect(() => {
    if (examSession) {
      const initialState = initializeStudentRegistrations(examSession);
      initialEnrollmentState.current = initialState; // Store for comparison
      setStudentClassRegistrations(initialState); // Update current state
    }
  }, [examSession]);

  const hasChanges = useMemo(() => {
    const initialKeys = Object.keys(initialEnrollmentState.current);
    const currentKeys = Object.keys(studentClassRegistrations);

    if (initialKeys.length !== currentKeys.length) return true;

    for (const key of currentKeys) {
      const studentId = Number(key);
      const initial = initialEnrollmentState.current[studentId];
      const current = studentClassRegistrations[studentId];

      if (!initial) return true;

      if (initial.scheduleId !== current.scheduleId) {
        return true;
      }
    }

    return false;
  }, [studentClassRegistrations]);

  const updateStudentRegistration = (
    studentId: number,
    classId: number | null,
  ) => {
    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      if (classId === null) {
        // If classId is null, remove the registration
        delete newRegistrations[studentId];
      } else {
        // Update or add the registration
        newRegistrations[studentId] = {
          ...prev[studentId], // Preserve existing fields
          studentId,
          scheduleId: classId,
        };
      }

      return newRegistrations;
    });
  };

  const registerUsersEnrollment =
    examSession?.studentsRegistrations.filter((item) => item.enrollment) ?? [];
  const registerUsersEnrollmentSet = new Set(
    registerUsersEnrollment.map((item) => item.studentId),
  );

  const registerUser = currentCourse?.details?.students.filter((item) =>
    registerUsersEnrollmentSet.has(item.id),
  );
  const [loading, setLoading] = useState(false);
  const handleOnApprove = async () => {
    const currentProgramId = globalProgramStore.getCurrentId();

    await upsertStudentsEnrollmentOperations(
      currentProgramId,
      currentCourse?.metadata.id,
      examId,
      examSession?.id,
      initialEnrollmentState.current,
      studentClassRegistrations,
      setSessionDetails,
      setLoading,
    );
  };

  return (
    <div>
      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        selectionMode={'multiple'}
        multiple={true}
        groupRowsBy="dsf"
        value={registerUser}
        scrollable
        onSelectionChange={(e) => {
          //setSelectedUsers(e.value as UserSummaryType[]);
        }}
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
      >
        <Column
          expander={false}
          header="שם"
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => (
            <label>
              {rowData.firstName} {rowData.lastName}
            </label>
          )}
        />

        <Column
          expander={false}
          header="ת.ז"
          field={'nationalId'}
          style={columnStyle}
        />

        <Column
          expander={false}
          header={''}
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => <div />}
        />

        {examSession &&
          examSession.classesSchedule.map((classItem: ClassSchedule) => (
            <Column
              key={classItem.classId}
              style={{ textAlign: 'right', height: '40px' }}
              header={classItem.className}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
              }}
              body={(data: UserSummaryType) => (
                <Checkbox
                  checked={
                    studentClassRegistrations[data.id]?.scheduleId ===
                    classItem.scheduleId
                  }
                  onChange={(e) => {
                    updateStudentRegistration(
                      data.id,
                      e.checked ? classItem.scheduleId : null,
                    );
                  }}
                />
              )}
            />
          ))}
      </DataTable>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '16px',
        }}
      >
        <Button
          style={{ height: '30px' }}
          label={'אישור'}
          loading={loading}
          disabled={!hasChanges}
          tooltip={hasChanges ? 'לחץ לאישור' : 'יש לבצע שינוי בשיבוץ'}
          tooltipOptions={{ showOnDisabled: true }}
          onClick={handleOnApprove}
        />
      </div>
    </div>
  );
};
